import { useState } from 'react'
import { Info } from 'lucide-react'
import type { ActivityCatalogEntry, Dashboard, ValidationResponse } from '../types'
import { useDecisionStore } from '../store/decision'
import { CATEGORY_STYLES } from '../lib/categoryColors'

interface Props {
  catalog: ActivityCatalogEntry[]
  dashboard: Dashboard
  validation: ValidationResponse | null
  submitting: boolean
  onValidate: () => void
  onSubmit: () => void
  onReset: () => void
  pendingForcedActivity: string | null
  alreadySubmitted: boolean
  sessionActive: boolean
}

export default function DecisionComposer(p: Props) {
  const draft = useDecisionStore((s) => s.draft)
  const setTempo = useDecisionStore((s) => s.setTempo)
  const setBoardRequest = useDecisionStore((s) => s.setBoardRequest)
  const toggleStart = useDecisionStore((s) => s.toggleStart)
  const toggleStop = useDecisionStore((s) => s.toggleStop)
  const toggleOneShot = useDecisionStore((s) => s.toggleOneShot)

  // Estimate committed $/mo from draft (crude client-side calc; server is authoritative).
  const byId = Object.fromEntries(p.catalog.map((c) => [c.id, c]))
  const startingMonthly = draft.programs_to_start.reduce(
    (a, id) => a + (byId[id]?.monthly_cost_k ?? 0), 0)
  const stoppingMonthly = draft.programs_to_stop.reduce(
    (a, id) => a + (byId[id]?.monthly_cost_k ?? 0), 0)
  const currentlyActiveMonthly = p.catalog
    .filter((c) => c.active && !draft.programs_to_stop.includes(c.id))
    .reduce((a, c) => a + c.monthly_cost_k, 0)
  const estimatedMonthlyK = currentlyActiveMonthly + startingMonthly

  const oneShotK = draft.one_shots.reduce(
    (a, id) => a + (byId[id]?.one_shot_cost_k ?? 0), 0)
  const startupK = draft.programs_to_start.reduce(
    (a, id) => a + (byId[id]?.one_shot_cost_k ?? 0), 0)
  const windDownK = draft.programs_to_stop.reduce(
    (a, id) => a + (byId[id]?.wind_down_cost_k ?? 0), 0)

  const quarterTotalK = estimatedMonthlyK * 3 + startupK + oneShotK + windDownK
  const quarterlyBudgetK = p.dashboard.monthly_budget_k * 3
  const overBudget = quarterTotalK > quarterlyBudgetK

  // Board Budget Request: the student can always ask. The only disclosed
  // rule is the 4-quarter cooldown after a successful grant. Whether the
  // board will approve otherwise depends on Leadership Confidence and
  // delivered Business Value — that's a judgment call the student has to
  // read from the dashboard, not a formula we hand them.
  const cooldownRemaining = p.dashboard.board_request_available_in_q ?? 0
  const cooldownBlocked = cooldownRemaining > 0
  const [showBoardHelp, setShowBoardHelp] = useState(false)

  return (
    <div className="card sticky top-32 max-h-[calc(100vh-9rem)] overflow-y-auto">
      <h2 className="text-base font-semibold mb-4">Decision — Quarter</h2>

      <section className="mb-4">
        <div className="stat-label mb-1">Urgency</div>
        <input
          type="range"
          min={0.5} max={3} step={0.5}
          value={draft.tempo}
          onChange={(e) => setTempo(parseFloat(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>Patient 0.5×</span>
          <span className="font-semibold text-slate-900">{draft.tempo.toFixed(1)}×</span>
          <span>Blitz 3×</span>
        </div>
      </section>

      <section className="mb-4 text-sm">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="board-budget-request"
            checked={draft.request_board_budget_increase}
            onChange={(e) => setBoardRequest(e.target.checked)}
            className="mt-1 cursor-pointer"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <label
                htmlFor="board-budget-request"
                className="font-medium cursor-pointer"
              >
                Board Budget Request
              </label>
              <button
                type="button"
                onClick={() => setShowBoardHelp((v) => !v)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                aria-label={showBoardHelp ? 'Hide details' : 'Show details'}
                aria-expanded={showBoardHelp}
              >
                <Info size={14} aria-hidden />
              </button>
            </div>
            {showBoardHelp && (
              <p className="mt-1 text-xs text-slate-500">
                Ask the board for +25% budget for 4 quarters. The board will
                only grant one every 4 quarters. Whether they approve depends
                on their read of your leadership confidence and delivered
                business value. Denial costs 8 Leadership Confidence.
              </p>
            )}
            {cooldownBlocked && (
              <div className="mt-1 rounded bg-amber-50 border border-amber-200 p-2">
                <div className="text-xs font-semibold text-amber-800">
                  Board granted an increase recently. Next window in {cooldownRemaining}q —
                  any ask now will be declined, costing 8 Confidence.
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {(draft.programs_to_start.length > 0 ||
        draft.programs_to_stop.length > 0 ||
        draft.one_shots.length > 0) && (
        <section className="mb-4 text-sm space-y-2">
          <div className="stat-label">Your draft</div>
          <PillGroup
            label="Starting"
            ids={draft.programs_to_start}
            byId={byId}
            tone="start"
            onRemove={toggleStart}
          />
          <PillGroup
            label="Stopping"
            ids={draft.programs_to_stop}
            byId={byId}
            tone="stop"
            onRemove={toggleStop}
          />
          <PillGroup
            label="One-shots"
            ids={draft.one_shots}
            byId={byId}
            tone="oneshot"
            onRemove={toggleOneShot}
          />
        </section>
      )}

      <section className="mb-4">
        <div className="stat-label mb-3">Budget this quarter</div>
        <BudgetSummary
          spentK={quarterTotalK}
          budgetK={quarterlyBudgetK}
          overBudget={overBudget}
        />
        <BudgetBreakdown
          programsK={estimatedMonthlyK * 3}
          startupK={startupK}
          oneShotK={oneShotK}
          windDownK={windDownK}
        />
      </section>

      {p.validation && !p.validation.valid && (
        <div className="mb-3 rounded bg-red-50 border border-red-200 p-2 text-xs text-danger">
          <div className="font-medium mb-1">Validation failed:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {p.validation.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      {p.validation && p.validation.valid && !p.alreadySubmitted && (
        <div className="mb-3 rounded bg-green-50 border border-green-200 p-2 text-xs text-ok">
          ✓ Decision accepted. Waiting for facilitator advance.
        </div>
      )}

      <div className="space-y-2">
        <button
          className="btn-primary w-full"
          onClick={p.onSubmit}
          disabled={p.submitting || p.alreadySubmitted || !p.sessionActive}
        >
          {p.submitting ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"
                aria-hidden
              />
              Submitting…
            </span>
          ) :
           p.alreadySubmitted ? 'Submitted (awaiting advance)' :
           !p.sessionActive ? 'Session not active' :
           'Submit Decision'}
        </button>
        <button className="btn-secondary w-full text-xs" onClick={p.onReset}>
          Reset draft
        </button>
      </div>
    </div>
  )
}

function PillGroup({
  label, ids, byId, tone, onRemove,
}: {
  label: string
  ids: string[]
  byId: Record<string, ActivityCatalogEntry>
  tone: 'start' | 'stop' | 'oneshot'
  onRemove: (id: string) => void
}) {
  if (ids.length === 0) return null
  const toneClass =
    tone === 'start'
      ? 'bg-brand-50 border-brand-200 text-brand-800 hover:bg-brand-100'
      : tone === 'stop'
      ? 'bg-red-50 border-red-200 text-red-800 hover:bg-red-100'
      : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {ids.map((id) => {
          const a = byId[id]
          const name = a?.name ?? id
          const dotClass = a ? CATEGORY_STYLES[a.category].dot : 'bg-slate-300'
          return (
            <button
              key={id}
              type="button"
              title={`${name} — click to remove`}
              onClick={() => onRemove(id)}
              className={
                'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 ' +
                'text-xs font-medium transition-colors ' +
                toneClass
              }
            >
              <span aria-hidden className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
              <span className="font-mono">{id}</span>
              <span aria-hidden className="opacity-60">×</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function BudgetSummary({
  spentK, budgetK, overBudget,
}: {
  spentK: number
  budgetK: number
  overBudget: boolean
}) {
  const availableK = budgetK - spentK
  const pct = budgetK > 0 ? Math.max(0, Math.min(100, (spentK / budgetK) * 100)) : 0
  const fmt = (n: number) => Math.round(Math.abs(n)).toLocaleString('en-US')

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div>
          <div
            className={`text-2xl font-semibold tabular-nums leading-tight ${
              overBudget ? 'text-danger' : 'text-slate-900'
            }`}
          >
            {overBudget ? '−' : ''}${fmt(availableK)}K
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">
            {overBudget ? 'Over budget' : 'Available'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums leading-tight text-slate-900">
            ${fmt(spentK)}K
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">
            Spent
          </div>
        </div>
      </div>
      <div
        className="h-2 rounded-full bg-slate-200 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={Math.round(budgetK)}
        aria-valuenow={Math.round(spentK)}
      >
        <div
          className={`h-full transition-all ${overBudget ? 'bg-danger' : 'bg-brand-600'}`}
          style={{ width: `${overBudget ? 100 : pct}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-slate-500 tabular-nums">
        ${fmt(spentK)}K of ${fmt(budgetK)}K
      </div>
    </div>
  )
}

function BudgetBreakdown({
  programsK, startupK, oneShotK, windDownK,
}: {
  programsK: number
  startupK: number
  oneShotK: number
  windDownK: number
}) {
  const items = [
    { label: 'Programs', value: programsK },
    { label: 'Start-up costs', value: startupK },
    { label: 'One-shots', value: oneShotK },
    { label: 'Wind-down', value: windDownK },
  ].filter((i) => i.value > 0)

  if (items.length === 0) return null

  return (
    <div className="mt-4 space-y-1 text-sm">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">
        Where it goes
      </div>
      {items.map((i) => (
        <div key={i.label} className="flex justify-between">
          <span className="text-slate-600">{i.label}</span>
          <span className="tabular-nums text-slate-700">
            ${Math.round(i.value).toLocaleString('en-US')}K
          </span>
        </div>
      ))}
    </div>
  )
}
